/**
 * The Naumene mark.
 * @file src/web/shared/NaumeneMark.tsx
 *
 * An N drawn as a single stroked path inside a rounded badge, with the gold
 * connector dot on the diagonal.
 *
 * ── Why it is an N, and what that cost ────────────────────────────────────
 * The geometry is adapted from `Mishu_Brand_Kit.html` v1.0, which drew an M
 * for the product's former name. The badge, the stroke weight, the round
 * caps and the colourways are the kit's, unchanged. The letterform is not:
 * it was rebuilt as an N when the product became Naumene.
 *
 * The dot moved with it, and the move is the one judgement call here. In the
 * M it sat in the vertex where the two strokes meet. An N has no middle
 * vertex, so it sits at the midpoint of the diagonal instead — which is
 * arguably more literal than the original, because on an N the diagonal IS
 * the connector between the two stems. It reads as a bead on the line rather
 * than a joint, and it only reads at all because the dot is wider than the
 * stroke (9 against 7). Narrow the gap and the dot disappears into the path.
 *
 * This is an adapted mark, not a designed one. A commissioned N would likely
 * differ in the diagonal's weight and where it meets the stems.
 *
 * ── Why the colours are variables ─────────────────────────────────────────
 * The kit ships five colourways and says to "choose by context". The two
 * that matter here are Primary (navy badge, white stroke, gold dot) and
 * Light (ivory badge, navy stroke, deep-gold dot). A navy badge on the dark
 * theme's navy-deep ground would very nearly vanish, so the choice has to
 * follow the theme rather than be fixed in the component.
 *
 * `--logo-badge` / `--logo-stroke` / `--logo-dot` are defined per theme in
 * styles/brand.css, which is where every other brand colour already lives.
 *
 * ── Sizing ────────────────────────────────────────────────────────────────
 * The kit sets a 24px floor for the icon alone. The default `size-8` (32px)
 * clears it. Below 24px use the favicon variant in public/favicon.svg, which
 * carries a heavier stroke so it survives the smaller raster.
 *
 * @llm-rule WHEN: Rendering the Naumene logo anywhere in the app
 * @llm-rule AVOID: Recolouring the mark inline — switch the theme variables
 * @llm-rule AVOID: Rendering it below 24px; use the favicon variant instead
 */

export function NaumeneMark({ className = 'size-8' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 96 96"
      className={`shrink-0 ${className}`}
      role="img"
      aria-label="Naumene"
    >
      <rect width="96" height="96" rx="20" fill="var(--logo-badge)" />
      <path
        d="M24,70 L24,30 L72,70 L72,30"
        stroke="var(--logo-stroke)"
        strokeWidth="7"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* The connector dot — the one gold element, and the point of the mark.
          (48,50) is the midpoint of the diagonal from (24,30) to (72,70). */}
      <circle cx="48" cy="50" r="4.5" fill="var(--logo-dot)" />
    </svg>
  );
}
